import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import AppLayout from './components/AppLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AddClient from './pages/AddClient';
import GenerateInvoice from './pages/GenerateInvoice';
import InvoiceView from './pages/InvoiceView';
import CRM from './pages/CRM';
import Contracts from './pages/Contracts';
import Proposals from './pages/Proposals';
import SalesTargets from './pages/SalesTargets';
import UserManagement from './pages/UserManagement';
import Account from './pages/Account';
import './App.css';

// Any authenticated user except sales (who are CRM-only)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role === 'sales') return <Navigate to="/crm" />;
  return <AppLayout>{children}</AppLayout>;
};

// CEO + HR only (e.g. User Management)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role === 'sales') return <Navigate to="/crm" />;
  if (user?.role !== 'ceo' && user?.role !== 'hr' && user?.role !== 'admin') return <Navigate to="/dashboard" />;
  return <AppLayout>{children}</AppLayout>;
};

// All authenticated roles (CEO, HR, Sales) — used for Contracts, Proposals, Targets
const AuthRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return <AppLayout>{children}</AppLayout>;
};

// Settings-aware route — CEO always passes; HR redirected if setting disabled; Sales blocked (CRM only)
const SettingsRoute = ({ children, settingKey }) => {
  const { isAuthenticated, user } = useAuth();
  const { settings, loadingSettings } = useSettings();

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (loadingSettings) return null;

  // CEO always has full access
  if (user?.role === 'ceo') return <AppLayout>{children}</AppLayout>;

  // Sales: can only access CRM
  if (user?.role === 'sales') {
    return settingKey === 'hr_can_see_crm'
      ? <AppLayout>{children}</AppLayout>
      : <Navigate to="/crm" />;
  }

  // HR: check whether CEO has enabled this section
  if (user?.role === 'hr' && settings[settingKey] === false) {
    return <Navigate to="/dashboard" />;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Redirect logged-in users — CEO/HR go to dashboard, Sales goes straight to CRM
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'sales' ? '/crm' : '/dashboard'} />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <Toaster />
        <Router>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/add-client" element={<ProtectedRoute><AddClient /></ProtectedRoute>} />
            <Route path="/generate-invoice" element={<SettingsRoute settingKey="hr_can_see_invoices"><GenerateInvoice /></SettingsRoute>} />
            <Route path="/invoice/:id" element={<SettingsRoute settingKey="hr_can_see_invoices"><InvoiceView /></SettingsRoute>} />
            <Route path="/crm" element={<SettingsRoute settingKey="hr_can_see_crm"><CRM /></SettingsRoute>} />
            <Route path="/contracts" element={<AuthRoute><Contracts /></AuthRoute>} />
            <Route path="/proposals" element={<AuthRoute><Proposals /></AuthRoute>} />
            <Route path="/targets" element={<AuthRoute><SalesTargets /></AuthRoute>} />
            <Route path="/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
            <Route path="/account" element={<AdminRoute><Account /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
